import { useContext, useEffect, useMemo, useState } from 'react';
import Stepper, { useStepper } from '../Stepper';

import { useNavigate } from 'react-router-dom';
import { OverlayTrigger, Popover, PopoverContent, Modal } from 'react-bootstrap';

import {
  EtaExportPortPage,
  AdditionalRequirements,
  EpoDirectionPage,
  ExitPortPage,
  EntryPortPage,
  EtaPortPage,
  CogPage,
  JobRefPage,
  EuOriginPage,
  InvoicesUploadPage,
  CongratsPage,
  LspPage,
  DocumentsUploadPage,
  PreferentialOrigin,
} from './Steps';
import { getDDAStatus, postEPOExport } from '../apiCalls';
import { useSecurity } from '../Security/security';
import Confirmation from './Steps/Confirmation';
import { StoreContext } from '../Store';
import { TransitMrn } from './Steps/TransitMrn';
import { useMutation } from '@tanstack/react-query';
import { postEpoHeaders } from '../../api/v2/epo';

export default function EpoExports() {
  const [redirectReason, setRedirectReason] = useState(null);
  const [dutyModalOpen, setDutyModalOpen] = useState(false);
  const [DDAStatus, setDDAStatus] = useState(0);
  const { securityState, validateRole } = useSecurity();
  const [trader, setTrader] = useState('');
  const isLsp = useMemo(() => validateRole({ department: 'WLP' }), []);
  const [movementRedirectReason, setMovementRedirectReason] = useState(null);

  const store = useContext(StoreContext);

  const postEpoHeadersMutation = useMutation({
    mutationFn: async (payload) => postEpoHeaders(payload),
  });

  const defaultData = useMemo(
    () => ({
      importId: '',
      exportId: '',
      direction: 'yes',
      entryport: { id: undefined },
      exitport: { id: undefined },
      importer: 'yes',
      selectedCategoryOfGoods: [],
      euPref: '',
      pref: '',
      hasTransitMrn: '',
      transitMrn: '',
      mrnDocuments: [],
      jobRef: '',
      date: '',
      lsp: '',
      fromAreaCode: '',
      toAreaCode: '',
      movementType: '',
      movementDirection: '',
      invoices: [],
      documents: [],
      certsList: [],
      importCountry: 'eu',
      exportCountry: 'eu',
      portModal: null,
      jiraContainer: '',
      epoUCN: '',
      epoBOL: '',
      tad: false,
      ens: false,
      addReqNeither: false,
      officeDest: '',
      inlandTransId: '',
      sealNo: '',
      dateText: '',
      categoryOptions: [],
      progress: true,
      ipaffsConfirmation: false,
      ehcConfirmation: false,
      catchConfirmation: false,
      ipaffsConfirmed: false,
      ehcConfirmed: false,
      catchConfirmed: false,
      confirmationModal: false,
      ipaffsConfirmationFinish: false,
      ehcConfirmationFinish: false,
      catchConfirmationFinish: false,
      openRedirectmodal: (text) => setRedirectReason(text),
      openDutyModal: () => setDutyModalOpen(true),
      openMovementRedirectModal: (text) => setMovementRedirectReason(text),
    }),
    []
  );

  useEffect(() => {
    async function fetchDDAStatus() {
      const data = await getDDAStatus(securityState?.companyid);
      if (data) {
        setDDAStatus(data[0]?.DDA);
      }
    }
    fetchDDAStatus();
  }, []);

  useEffect(() => {
    store.epoProgress[1](true);
    store.lockDirection[1](false);
  }, []);

  const setDDAFlag = (DDA) => setDDAStatus(DDA);

  const covertToCamelCase = (sentence) => {
    if (sentence) {
      const words = sentence.split(' ');
      const capitalizedWords = words.map((word) => {
        const firstLetter = word.charAt(0).toUpperCase();
        const restOfWord = word.slice(1).toLowerCase();
        return firstLetter + restOfWord;
      });
      return capitalizedWords.join(' ');
    }
  };

  const handleEmitData = async (data) => {
    const {
      officeDest,
      sealNo,
      inlandTransId,
      tad,
      ens,
      importId,
      exportId,
      entryport,
      exitport,
      importer,
      euPref,
      jobRef,
      date,
      invoices,
      lsp,
      confirmedNoCG,
      certsList,
      documents,
      movementType,
      ipaffsConfirmationFinish,
      transitMrn,
      mrnDocuments,
      portModal,
      jiraContainer,
      epoUCN,
      epoBOL,
    } = data;
    try {
      const fd = new FormData();

      if (movementType === 'Export') {
        fd.append('exitPortId', exitport?.id);
      }
      if (movementType === 'Import') {
        fd.append('entryPortId', entryport?.id);
        fd.append('portModal', portModal);
        fd.append('jiraContainer', jiraContainer);
        fd.append('epoUCN', epoUCN);
        fd.append('epoBOL', epoBOL);
      }
      fd.append('type', movementType);
      fd.append('importId', importId);
      fd.append('exportId', exportId);
      fd.append('importer', importer);
      fd.append('euPref', euPref);
      fd.append('jobRef', jobRef);
      fd.append('etaDate', date);
      fd.append('lsp', lsp);
      fd.append('tad', tad);
      fd.append('CABIEIpaffs', ipaffsConfirmationFinish);
      fd.append('ens', ens);
      fd.append('sealNo', sealNo);
      fd.append('officeDest', officeDest);
      fd.append('inlandTransId', inlandTransId);
      fd.append('controlledGoods', confirmedNoCG == 'NCG' ? 'N' : 'Y');
      fd.append('certsList', JSON.stringify(certsList));
      fd.append('transitMRN', transitMrn);
      for (const file of invoices) {
        fd.append('invoices', file);
      }
      for (const file of [...documents, ...mrnDocuments]) {
        fd.append('documents', file.file);
        fd.append('documentNames', file.name);
      }

      const result = await postEpoHeadersMutation.mutateAsync(fd);
      return result;
    } catch (error) {
      console.log('Error submitting EPO headers:', error);
    }
  };

  return (
    <>
      <DutyRedirectModal show={dutyModalOpen} onHide={() => setDutyModalOpen(false)} />
      <Stepper
        showFuture={false}
        emitData={handleEmitData}
        defaultData={defaultData}
        stepStyle={{
          borderRadius: '20px',
          padding: '0.5rem',
          minWidth: '500px',
          minHeight: '475px',
        }}
      >
        <RedirectModal
          redirectReason={redirectReason}
          onHide={() => {
            setRedirectReason(null);
          }}
          onContinue={() => {
            setRedirectReason(null);
          }}
        />
        <DirectionRedirectModal
          redirectReason={movementRedirectReason}
          onHide={() => {
            setMovementRedirectReason(null);
          }}
        />

        <main
          className="d-flex w-100 mx-auto justify-content-center"
          style={isLsp && trader ? { marginTop: '3rem' } : { marginTop: '2rem' }}
        >
          <div className="d-flex flex-column" style={{ borderRadius: '0.9rem' }}>
            {validateRole({ department: 'WLP' }) && (
              <div
                className="mb-4 themed-text"
                style={{
                  fontSize: '32px',
                  marginLeft: '5px',
                  lineHeight: '20px',
                }}
              >
                {covertToCamelCase(trader)}
              </div>
            )}
            <div className="d-flex">
              {validateRole({ department: 'WLP' }) && (
                <LspPage DDAStatus={setDDAFlag} showTrader={(trader) => setTrader(trader)} />
              )}
              <EpoDirectionPage DDAStatus={DDAStatus} />
              {DDAStatus === 0 && <PreferentialOrigin />}
              {DDAStatus === 0 && <EuOriginPage />}
              <TransitMrn DDAStatus={DDAStatus} />
              <EntryPortPage />
              <ExitPortPage />
              <AdditionalRequirements />
              <CogPage />
              <JobRefPage />
              <EtaPortPage />
              <EtaExportPortPage />
              <DocumentsUploadPage />
              <InvoicesUploadPage />
              <Confirmation showTrader={() => setTrader('')} DDAStatus={DDAStatus} />
              <CongratsPage validateRole={validateRole} isDanAccount={DDAStatus} />
            </div>
          </div>
          <div style={{ paddingLeft: '20px', marginTop: isLsp ? '8.3rem' : '5.56rem' }}>
            <div className={'mb-3 themed-text'}></div>
            <Stepper.StepList
              title={
                <div
                  className="epo-steps-stepper-header"
                  style={{
                    paddingTop: '0.5rem',
                    paddingLeft: '1.95rem',
                    paddingBottom: '0.7rem',
                  }}
                ></div>
              }
            />
          </div>
        </main>
      </Stepper>
    </>
  );
}

export const EPOTooltip = () => {
  return (
    <OverlayTrigger
      placement="right"
      trigger={['hover', 'focus']}
      overlay={
        <Popover style={{ minWidth: '35rem', marginRight: '0.5rem !important' }}>
          <PopoverContent>
            <h6>Duty Calculation Required</h6>
            <p>
              If your goods are of mixed origin. you will need to calculate your duty if you do not have a registered
              DAN account. If you have a DAN account, we will automatically calculate the duty for you.
            </p>
          </PopoverContent>
        </Popover>
      }
    >
      <div className="info" style={{ width: '1rem', marginRight: '0.5rem' }}></div>
    </OverlayTrigger>
  );
};

const RedirectModal = ({ redirectReason, onHide, onContinue }) => {
  const { stepForward } = useStepper();
  function cancelClick() {
    onHide();
  }
  function onContinue() {
    stepForward();
    onHide();
  }
  return (
    <Modal show={!!redirectReason} centered size={'md'} onHide={onHide} animation={false}>
      <Modal.Header>
        <Modal.Title className="d-flex gap-3 align-items-center py-2">
          <div className="warning" /> <span>{redirectReason?.title}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-4">{redirectReason?.body}</Modal.Body>
      <Modal.Footer
        class="d-flex flex-row py-4 px-3 gap-3 border-top bg-light justify-content-end align-items-center"
        style={{
          borderBottomLeftRadius: '20px',
          borderBottomRightRadius: '20px',
        }}
      >
        <button class="stepper-button" onClick={cancelClick}>
          Cancel
        </button>
        {redirectReason?.showContinue && (
          <button class="stepper-button yellow" onClick={() => onContinue()}>
            Continue
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const DirectionRedirectModal = ({ redirectReason, onHide }) => {
  const { localData, setDataByKey, unvisitStep } = useStepper();
  const store = useContext(StoreContext);

  function cancelClick() {
    onHide();
  }

  async function resetClick() {
    onHide();
    store.lockDirection[1](false);
    setDataByKey('confirmedNoCG', undefined);
    setDataByKey('date', '');
    setDataByKey('exportId', '');
    setDataByKey('importId', '');
    setDataByKey('portModal', null);
    setDataByKey('entryport', { id: undefined });
    setDataByKey('exitport', { id: undefined });
    setDataByKey('jobRef', '');
    setDataByKey('addReqNeither', false);
    setDataByKey('tad', false);
    setDataByKey('ens', false);
    setDataByKey('officeDest', '');
    setDataByKey('inlandTransId', '');
    setDataByKey('sealNo', '');
    setDataByKey('dateText', '');
    setDataByKey('categoryOptions', []);
    setDataByKey('categoryOptionsDefault', []);
    setDataByKey('selectedCategoryOfGoods', []);
    setDataByKey('invoices', []);
    setDataByKey('documents', []);
    setDataByKey('certsList', []);
    unvisitStep('9. Documents');
    unvisitStep('6. JobReference');
    unvisitStep('10. Commercial Documents');
    unvisitStep('8. EtaExportPort');
    unvisitStep('7. EtaPort');
    unvisitStep('4. AdditionalRequirements');
    unvisitStep('3. Entry Port');
    unvisitStep('2. Exit Port');
    unvisitStep('5. CategoryOfGoods');
    unvisitStep('eu_origin');

    const fd = new FormData();
    fd.append('to', 'false');

    if (localData.movementType === 'Export') {
      setDataByKey('movementType', 'Import');
      setDataByKey('exportCountry', 'eu');
      store.exOrImport[1]('EPO - ' + 'Import');
      fd.append('movementType', 'Import');
      const updateFlags = await postEPOExport('routeChoice', fd);
      if (updateFlags) {
        store.fromFlagOptions[1](updateFlags);
      }
      const fd2 = new FormData();
      fd2.append('to', 'true');
      fd2.append('movementType', 'Import');

      const updateImportDropdown = await postEPOExport('routeChoice', fd2);
      if (updateImportDropdown) {
        store.toFlagOptions[1](updateImportDropdown);
        updateImportDropdown.forEach((e) => {
          if (e.abbreviation === 'gb') {
            setDataByKey('importCountry', 'gb');
            setDataByKey('importId', e.id);
            setDataByKey('toAreaCode', e.areaCode);
          }
        });
      }
    } else {
      setDataByKey('movementType', 'Export');
      setDataByKey('importCountry', 'eu');
      setDataByKey('exportCountry', 'gb');
      store.exOrImport[1]('EPO - ' + 'Export');
      fd.append('movementType', 'Export');

      const updateFlags = await postEPOExport('routeChoice', fd);
      if (updateFlags) {
        store.fromFlagOptions[1](updateFlags);
        updateFlags.forEach(async (e) => {
          if (e.abbreviation === 'gb') {
            setDataByKey('exportId', e.id);
            setDataByKey('fromAreaCode', e.areaCode);
            const fd1 = new FormData();
            fd1.append('from', e.id);
            fd1.append('to', 'true');
            fd1.append('movementType', 'Export');
            const updateImportDropdown = await postEPOExport('routeChoice', fd1);
            if (updateImportDropdown) {
              store.toFlagOptions[1](updateImportDropdown);
            }
          }
        });
      }
    }
  }

  return (
    <Modal show={!!redirectReason} centered size={'md'} onHide={onHide} animation={false}>
      <Modal.Header>
        <Modal.Title className="d-flex gap-3 align-items-center py-2">
          <div className="warning" /> <span>{redirectReason?.title}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-4">{redirectReason?.body}</Modal.Body>
      <Modal.Footer
        class="d-flex flex-row py-4 px-3 gap-3 border-top bg-light justify-content-between align-items-center"
        style={{
          borderBottomLeftRadius: '20px',
          borderBottomRightRadius: '20px',
        }}
      >
        <button class="stepper-button" onClick={cancelClick}>
          Cancel
        </button>

        <button class="stepper-button yellow" onClick={resetClick}>
          Ok
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const DutyRedirectModal = ({ show, onHide }) => {
  const navigate = useNavigate();

  function cancelClick() {
    onHide();
  }
  function confirmClick() {
    navigate('/Userprofile/compliance');
  }

  return (
    <Modal show={show} centered size={'md'} onHide={onHide} animation={false}>
      <Modal.Header>
        <Modal.Title>More Information Required</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Sorry, without your C1207N form and Duty Deferment Number we aren't able to process Non-Preferential goods via
          EPO.
        </p>

        <p>If you have this information now, please add using the link below.</p>
      </Modal.Body>
      <Modal.Footer
        class="d-flex flex-row py-3 px-3 gap-3 border-top bg-light justify-content-between align-items-center"
        style={{
          borderBottomLeftRadius: '20px',
          borderBottomRightRadius: '20px',
        }}
      >
        <button class="stepper-button" onClick={cancelClick}>
          Close
        </button>
        <button class="stepper-button" onClick={confirmClick}>
          Add
        </button>
      </Modal.Footer>
    </Modal>
  );
};
